import { ArrowRight, Minus, Plus, ShoppingBasket, Trash2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { DishImage } from '@/components/DishImage';
import { FREE_DELIVERY_FROM, useCart } from '@/contexts/CartContext';
import { formatKES } from '@/lib/format';
import { getDeliveryEstimate } from '@/lib/time';

interface CartDrawerProps {
  onCheckout: () => void;
}

export function CartDrawer({ onCheckout }: CartDrawerProps) {
  const { lines, count, subtotal, deliveryFee, total, isCartOpen, setCartOpen, setQty, removeItem } =
    useCart();

  const freeDeliveryProgress = Math.min(100, (subtotal / FREE_DELIVERY_FROM) * 100);
  const remainingForFree = FREE_DELIVERY_FROM - subtotal;

  return (
    <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md" aria-describedby={undefined}>
        <SheetHeader className="border-b border-border px-6 py-5">
          <SheetTitle className="flex items-center gap-2.5 font-display text-2xl font-black">
            <ShoppingBasket className="size-6 text-primary" />
            Your Order
            {count > 0 && (
              <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-primary-foreground">
                {count}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            {count > 0 ? `Estimated arrival: ${getDeliveryEstimate()} EAT` : 'Your basket is waiting to be filled.'}
          </SheetDescription>
        </SheetHeader>

        {lines.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 py-16 text-center">
            <span className="flex size-20 items-center justify-center rounded-full bg-muted">
              <ShoppingBasket className="size-9 text-muted-foreground/60" />
            </span>
            <div>
              <p className="font-display text-xl font-bold">Basket iko empty</p>
              <p className="mx-auto mt-2 max-w-xs text-sm text-muted-foreground">
                Add some delicious Kenyan dishes and they'll show up here, ready for checkout.
              </p>
            </div>
            <Button onClick={() => setCartOpen(false)} className="rounded-full px-6 font-bold">
              Browse the menu
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ul className="divide-y divide-border">
                {lines.map(({ item, qty }) => (
                  <li key={item.id} className="flex gap-3.5 py-4">
                    <DishImage src={item.image} alt={item.name} className="size-16 shrink-0 rounded-xl" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-sm font-bold">{item.name}</p>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`Remove ${item.name} from cart`}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{formatKES(item.price)} each</p>
                      <div className="mt-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-1 rounded-full bg-muted p-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setQty(item.id, qty - 1)}
                            className="size-7 rounded-full"
                            aria-label={`Decrease quantity of ${item.name}`}
                          >
                            <Minus className="size-3.5" />
                          </Button>
                          <span className="min-w-6 text-center text-sm font-black tabular-nums">{qty}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setQty(item.id, qty + 1)}
                            className="size-7 rounded-full"
                            aria-label={`Increase quantity of ${item.name}`}
                          >
                            <Plus className="size-3.5" />
                          </Button>
                        </div>
                        <p className="text-sm font-black tabular-nums">{formatKES(item.price * qty)}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-border bg-muted/40 px-6 py-5">
              {deliveryFee > 0 ? (
                <div className="mb-4 rounded-xl border border-border bg-card p-3.5">
                  <div className="flex items-center gap-2 text-xs font-semibold">
                    <Truck className="size-4 text-primary" />
                    Add <span className="font-black text-primary">{formatKES(remainingForFree)}</span> more for FREE delivery
                  </div>
                  <Progress value={freeDeliveryProgress} className="mt-2.5 h-1.5" />
                </div>
              ) : (
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-leaf/30 bg-leaf/10 p-3.5 text-xs font-bold text-secondary dark:text-secondary-foreground">
                  <Truck className="size-4" />
                  Hongera! You've unlocked FREE delivery.
                </div>
              )}

              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Subtotal</dt>
                  <dd className="font-semibold tabular-nums">{formatKES(subtotal)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Delivery</dt>
                  <dd className="font-semibold tabular-nums">
                    {deliveryFee === 0 ? <span className="text-leaf dark:text-marigold">FREE</span> : formatKES(deliveryFee)}
                  </dd>
                </div>
                <div className="flex justify-between border-t border-border pt-2.5 text-base">
                  <dt className="font-black">Total</dt>
                  <dd className="font-black tabular-nums text-primary">{formatKES(total)}</dd>
                </div>
              </dl>

              <Button
                size="lg"
                onClick={() => {
                  setCartOpen(false);
                  onCheckout();
                }}
                className="mt-4 w-full rounded-full text-base font-bold shadow-lg shadow-primary/25"
              >
                Checkout — {formatKES(total)}
                <ArrowRight className="size-4" />
              </Button>
              <p className="mt-2.5 text-center text-xs text-muted-foreground">
                Pay securely with M-Pesa, Tando or cash on delivery
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
